import {
  Charge,
  ChargeStatus,
  PaymentsService,
  RegistrationPeriod,
  VehicleInfo,
} from '@/@types/generated'
import { useError } from '@/hooks/useError'
import { useHazmat } from '@/modules/carriers/hooks/useHazmat'
import type { Hazmat } from '@/modules/carriers/lib/hazmatTarget'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

export type RenewalRegistrationPeriods =
  | RegistrationPeriod.ONE_YEAR
  | RegistrationPeriod.TWO_YEARS
  | null

export interface RenewalVehicle extends VehicleInfo {
  addToCurrentRegistration: boolean
}

export interface RenewalCharge extends Charge {
  minimumDueAmount: number
}

interface RenewalContextProps {
  readonly vehicles: RenewalVehicle[]
  setVehicles(vehicles: RenewalVehicle[]): void

  readonly registrationPeriod: RenewalRegistrationPeriods
  setRegistrationPeriod(period: RenewalRegistrationPeriods): void

  readonly outstandingCharges: RenewalCharge[] | null
  readonly overdue?: {
    readonly min: number
    readonly max: number
  }
  readonly isReRegistration: boolean
  readonly isWithinCurrentRegistrationPeriod: boolean

  /** The certificate's current Hazmat determination, once loaded. */
  readonly hazmat: Hazmat | null
  /**
   * CRD-009: the Hazmat status chosen for this renewal, applied when the payment completes.
   * Null keeps the certificate's current status.
   */
  readonly hazmatChoice: boolean | null
  setHazmatChoice(isHazmat: boolean): void
}

const RenewalContext = createContext<RenewalContextProps>(undefined!)

interface RenewalProviderCertificate {
  issueDate?: string | null
  expirationDate?: string | null
  certificateNumber?: string
  isWithinCurrentRegistrationPeriod?: boolean
}

interface RenewalProviderProps {
  businessEntityId: string
  carrierId?: string
  isReRegistration?: boolean
  certificate: RenewalProviderCertificate
}

export function RenewalProvider({
  businessEntityId,
  carrierId,
  isReRegistration,
  certificate,
  children,
}: React.PropsWithChildren<RenewalProviderProps>) {
  const [vehicles, setVehicles] = useState<RenewalVehicle[]>([])
  const [registrationPeriod, setRegistrationPeriod] =
    useState<RenewalRegistrationPeriods>(null)
  const [outstandingCharges, setOutstandingCharges] = useState<Charge[] | null>(
    null
  )
  const { showError } = useError()
  const { hazmat } = useHazmat({ carrierId: carrierId ?? '' }, !!carrierId)
  const [hazmatChoice, setHazmatChoice] = useState<boolean | null>(null)

  const _setVehicles = useCallback(
    (newVehicles: RenewalVehicle[]) => {
      const vinSet = new Set<string>()
      const newUnique = newVehicles.filter((v) => {
        if (v.vin && vinSet.has(v.vin)) {
          showError(`Duplicate vehicle with VIN ${v.vin} was not added.`)
          return false
        }
        if (v.vin) vinSet.add(v.vin)
        return true
      })
      setVehicles(newUnique)
    },
    [showError]
  )

  const minRate = registrationPeriod === RegistrationPeriod.ONE_YEAR ? 0.5 : 1
  const loadOutstandingCharges = useCallback(async () => {
    if (!businessEntityId) return

    try {
      const { charges } = await PaymentsService.listCharges(
        undefined,
        businessEntityId,
        [ChargeStatus.OUTSTANDING]
      )
      setOutstandingCharges(charges)
    } catch (error) {
      showError(error, 'Failed to load outstanding charges.')
    }
  }, [businessEntityId, showError])

  useEffect(() => {
    loadOutstandingCharges()
  }, [loadOutstandingCharges])

  const totalOutstanding = useMemo(
    () =>
      outstandingCharges?.reduce(
        (sum, { unpaidAmount }) => sum + unpaidAmount,
        0
      ) || 0,
    [outstandingCharges]
  )
  const outstandingChargesWithMins = useMemo(
    () =>
      (outstandingCharges || []).map((charge) => ({
        ...charge,
        minimumDueAmount: charge.unpaidAmount * minRate,
      })),
    [outstandingCharges, minRate]
  )
  const min = totalOutstanding * minRate
  const max = totalOutstanding

  return (
    <RenewalContext.Provider
      value={{
        vehicles,
        setVehicles: _setVehicles,
        registrationPeriod,
        setRegistrationPeriod,
        outstandingCharges: outstandingChargesWithMins,
        overdue: totalOutstanding > 0 ? { min, max } : undefined,
        isReRegistration: isReRegistration ?? false,
        isWithinCurrentRegistrationPeriod:
          certificate.isWithinCurrentRegistrationPeriod ?? false,
        hazmat,
        hazmatChoice,
        setHazmatChoice,
      }}
    >
      {children}
    </RenewalContext.Provider>
  )
}

export function useRenewal() {
  const context = useContext(RenewalContext)
  if (context === undefined)
    throw new Error('useRenewal must be used within a RenewalProvider route')

  return context
}
